import CarouselV2 from '../../elements/CarouselV2/CarouselV2'
import cx from 'classnames'
import styles from './CoverflowCarousel.module.scss'
import { CardOverlay, Heading, Button } from '~elements'
import { CardOverlayProps } from '~types'
import { Settings } from 'react-slick'
import { ButtonProps } from '~types'
import { useRef, MouseEvent, useState } from 'react'
import Slider from 'react-slick'
import { useModalExists } from 'lib/context/modalContext'

export interface CoverFlowCarouselProps {
  slides: CardOverlayProps[]
  headline?: string
  cta?: ButtonProps
}

function CoverFlowCarousel({ slides, headline, cta }: CoverFlowCarouselProps) {
  const sliderRef = useRef<Slider>(null)
  const initialSlide = 0
  const [centerSlideIndex, setCenterSlideIndex] = useState(initialSlide)
  const [isSliderTransitioning, setIsSliderTransitioning] = useState(false)
  const navigationRef = useRef<{
    goToNext: () => void
    goToPrev: () => void
    goToSlide: (index: number) => void
  } | null>(null)
  const { isAnyModalOpen } = useModalExists()

  const carouselSettings: Settings = {
    slidesToScroll: 1,
    centerMode: true,
    centerPadding: '24px',
    infinite: true,
    speed: 500,
    initialSlide: initialSlide,
    swipe: !isAnyModalOpen,
    beforeChange: () => setIsSliderTransitioning(true),
    afterChange: (current: number) => {
      setCenterSlideIndex(current)
      setIsSliderTransitioning(false)
    }
  }

  const handleCardClick = (e: MouseEvent<HTMLDivElement>, slideIndex: number) => {
    if (slideIndex !== centerSlideIndex && navigationRef.current) {
      // Get the click position relative to the viewport
      const clickX = e.clientX
      const viewportCenter = window.innerWidth / 2

      // Determine direction based on mouse position
      if (clickX > viewportCenter) {
        navigationRef.current.goToNext()
      } else {
        navigationRef.current.goToPrev()
      }
    }
    // If the card is active (center), the default CardOverlay behavior will handle navigation to URL
  }

  return (
    <div className={styles['coverflow-carousel-module']} data-testid='hero-carousel-module'>
      {headline && (
        <div className={styles['coverflow-heading']}>
          <Heading level={2} justify='center' title={headline} noMarginTop />
        </div>
      )}
      <div className={cx(styles['coverflow-carousel'])}>
        <CarouselV2
          slides={slides}
          className={styles['carousel']}
          showArrowsBreakpointUp='sm'
          showAllShowsButton={true}
          carouselSettings={carouselSettings}
          useDotArrows={true}
          sliderRef={sliderRef}
          sliderContainerClass={styles['slider-container']}
          slideChangeDelay={400}
          onNavigationReady={navigation => {
            navigationRef.current = navigation
          }}
          renderSlide={(slide: CardOverlayProps, index, isTransitioning) => (
            // Both isTransitioning || isSliderTransitioning are needed
            // to ensure content hides during slide change
            // isTransitioning marks the start of the change,
            // and isSliderTransitioning marks the end
            <CardOverlay
              key={index}
              className={styles['card-overlay']}
              contentClassname={styles['card-overlay-content']}
              {...slide}
              onCardClick={e => handleCardClick(e, index)}
              isCarouselSlide={true}
              hideContent={index !== centerSlideIndex || isTransitioning || isSliderTransitioning}
              showModalOnClick
            />
          )}
        />
      </div>
      {cta && <Button {...cta} />}
    </div>
  )
}

export default CoverFlowCarousel
